import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchClasses, fetchClass, createClass, enrollClass,
  cancelEnrollment, attendEnrollment, fetchMembers,
} from "../services/api";

// Group classes (gym 1:N): schedule a class with a capacity cap, enroll members
// (extra go on the waitlist), and when someone cancels the first waitlisted
// person is auto-promoted into the room.

const S = {
  en: {
    title: "Classes", sub: "Group classes with capacity and an auto-promoting waitlist.",
    new_class: "New class", name: "Class name", when: "Date & time", dur: "Minutes",
    cap: "Capacity", inst: "Instructor", create: "Create class", cancel: "Cancel",
    upcoming: "Upcoming classes", none: "No upcoming classes yet.",
    booked: "booked", wait: "waitlist", left: "spots left", full: "FULL",
    roster: "Roster", waitlist: "Waitlist", enroll: "Enroll", add: "Add",
    search_m: "Search member…", walkin_name: "or type a name", attended: "attended",
    mark: "Attended", remove: "Remove", promoted: "Waitlist promoted ✓", pick: "Pick a class to see its roster.",
    empty_roster: "Nobody enrolled yet.",
  },
  am: {
    title: "ክፍሎች", sub: "በአቅም እና ራስ-ሰር በሚያድግ የመጠባበቂያ ዝርዝር የቡድን ክፍሎች።",
    new_class: "አዲስ ክፍል", name: "የክፍል ስም", when: "ቀን እና ሰዓት", dur: "ደቂቃዎች",
    cap: "አቅም", inst: "አሰልጣኝ", create: "ክፍል ፍጠር", cancel: "ሰርዝ",
    upcoming: "የሚመጡ ክፍሎች", none: "ገና የሚመጣ ክፍል የለም።",
    booked: "ተይዟል", wait: "መጠባበቂያ", left: "ቀሪ ቦታ", full: "ሞልቷል",
    roster: "ተሳታፊዎች", waitlist: "መጠባበቂያ", enroll: "አስመዝግብ", add: "ጨምር",
    search_m: "አባል ፈልግ…", walkin_name: "ወይም ስም ይተይቡ", attended: "ተገኝቷል",
    mark: "ተገኝቷል", remove: "አስወግድ", promoted: "ከመጠባበቂያ ወደ ላይ ተወስዷል ✓", pick: "ተሳታፊዎችን ለማየት ክፍል ይምረጡ።",
    empty_roster: "ገና የተመዘገበ የለም።",
  },
  om: {
    title: "Barnoota", sub: "Barnoota garee dandeettii fi eeggata ofumaan ol guddatuun.",
    new_class: "Barnoota haaraa", name: "Maqaa barnootaa", when: "Guyyaa & sa'aa", dur: "Daqiiqaa",
    cap: "Dandeettii", inst: "Leenjisaa", create: "Barnoota uumi", cancel: "Dhiisi",
    upcoming: "Barnoota dhufan", none: "Ammatti barnootni dhufu hin jiru.",
    booked: "qabame", wait: "eeggata", left: "iddoo hafe", full: "GUUTE",
    roster: "Hirmaattota", waitlist: "Eeggata", enroll: "Galmeessi", add: "Dabali",
    search_m: "Miseensa barbaadi…", walkin_name: "yookiin maqaa galchi", attended: "argame",
    mark: "Argame", remove: "Balleessi", promoted: "Eeggata irraa ol guddate ✓", pick: "Hirmaattota ilaaluuf barnoota filadhu.",
    empty_roster: "Ammatti kan galmaa'e hin jiru.",
  },
};

export default function ClassesPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;

  const [classes, setClasses] = useState([]);
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState(null);        // selected class detail (with roster)
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", start_at: "", duration_min: 60, capacity: 20, instructor_name: "" });
  const [eq, setEq] = useState("");            // member enroll search
  const [ename, setEname] = useState("");      // walk-in name

  const load = useCallback(async () => {
    try { setClasses(await fetchClasses({ upcoming: true })); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load classes."); }
  }, []);
  useEffect(() => { load(); fetchMembers().then(setMembers).catch(() => {}); }, [load]);

  const openClass = async (id) => {
    try { setSel(await fetchClass(id)); } catch { /* ignore */ }
  };

  const create = async () => {
    if (!form.name.trim() || !form.start_at) { setErr("Name and time are required."); return; }
    try {
      const c = await createClass({ ...form, start_at: new Date(form.start_at).toISOString(),
        duration_min: Number(form.duration_min) || null, capacity: Number(form.capacity) || 1 });
      setShowForm(false); setForm({ name: "", start_at: "", duration_min: 60, capacity: 20, instructor_name: "" });
      await load(); openClass(c.id);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create the class."); }
  };

  const enrollMember = async (m) => { await doEnroll({ membership_id: m.id }); setEq(""); };
  const enrollName = async () => { if (!ename.trim()) return; await doEnroll({ name: ename.trim() }); setEname(""); };
  const doEnroll = async (body) => {
    try { const r = await enrollClass(sel.id, body); await openClass(sel.id); await load();
      if (r.enrollment.status === "waitlisted") setFlash(t("waitlist")); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not enroll."); }
  };
  const cancelE = async (eid) => {
    try { const r = await cancelEnrollment(sel.id, eid); setSel(r); await load();
      if (r.promoted_enrollment_id) { setFlash(t("promoted")); setTimeout(() => setFlash(""), 3000); } }
    catch (e) { setErr(e?.response?.data?.detail || "Could not cancel."); }
  };
  const attendE = async (eid) => {
    try { setSel(await attendEnrollment(sel.id, eid)); } catch (e) { setErr(e?.response?.data?.detail || "Could not mark attended."); }
  };

  const memberMatches = useMemo(() => {
    const s = eq.trim().toLowerCase();
    if (!s) return [];
    return members.filter((m) => (m.name || "").toLowerCase().includes(s) || (m.phone || "").includes(s)).slice(0, 6);
  }, [members, eq]);

  const roster = (sel && sel.roster) || [];
  const booked = roster.filter((r) => r.status === "booked" || r.status === "attended");
  const waiting = roster.filter((r) => r.status === "waitlisted");

  return (
    <div className="page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div><h1 style={{ marginBottom: 2 }}>🧘 {t("title")}</h1><p className="muted" style={{ marginTop: 0 }}>{t("sub")}</p></div>
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>＋ {t("new_class")}</button>
      </div>

      {err && <div style={{ background: "#fdecec", color: "#b02a2a", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{err}</div>}
      {flash && <div style={{ background: "#f2fbf4", color: "#2b8a3e", border: "1px solid #cdeed5", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{flash}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16, margin: "10px 0" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: "2 1 200px" }}><label>{t("name")}</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Spin / Yoga / HIIT" style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "1 1 180px" }}><label>{t("when")}</label>
              <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "0 1 90px" }}><label>{t("dur")}</label>
              <input type="number" min="0" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "0 1 90px" }}><label>{t("cap")}</label>
              <input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "1 1 150px" }}><label>{t("inst")}</label>
              <input value={form.instructor_name} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={create}>{t("create")}</button>
            <button className="btn" onClick={() => setShowForm(false)}>{t("cancel")}</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start", marginTop: 8 }}>
        {/* class list */}
        <div style={{ flex: "1 1 320px" }}>
          <h3 style={{ marginTop: 0 }}>{t("upcoming")}</h3>
          {classes.length === 0 && <p className="muted">{t("none")}</p>}
          {classes.map((c) => (
            <div key={c.id} onClick={() => openClass(c.id)}
              style={{ background: "#fff", border: "1px solid " + (sel && sel.id === c.id ? "#1f6f4a" : "#eef0f3"), borderRadius: 10, padding: "10px 12px", marginBottom: 8, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div><strong>{c.name}</strong> <span className="muted" style={{ fontSize: 12 }}>· {c.time}{c.instructor_name ? ` · ${c.instructor_name}` : ""}</span></div>
                <span style={{ fontSize: 12, fontWeight: 700, color: c.full ? "#b02a2a" : "#2b8a3e" }}>
                  {c.full ? t("full") : `${c.spots_left} ${t("left")}`}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                {c.booked}/{c.capacity} {t("booked")}{c.waitlisted ? ` · ${c.waitlisted} ${t("wait")}` : ""}
              </div>
            </div>
          ))}
        </div>

        {/* roster of the selected class */}
        <div style={{ flex: "1 1 320px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16 }}>
          {!sel && <p className="muted">{t("pick")}</p>}
          {sel && (
            <>
              <h3 style={{ marginTop: 0 }}>{sel.name} <span className="muted" style={{ fontSize: 13 }}>· {sel.time} · {sel.booked}/{sel.capacity}</span></h3>

              {/* enroll */}
              <div style={{ marginBottom: 10 }}>
                <input value={eq} onChange={(e) => setEq(e.target.value)} placeholder={t("search_m")} style={{ width: "100%", padding: 8, marginBottom: 4 }} />
                {memberMatches.map((m) => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
                    <span>{m.name} <span className="muted" style={{ fontSize: 12 }}>{m.plan_name}</span></span>
                    <button className="btn btn-primary" onClick={() => enrollMember(m)} style={{ padding: "3px 10px" }}>{t("enroll")}</button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <input value={ename} onChange={(e) => setEname(e.target.value)} placeholder={t("walkin_name")} style={{ flex: 1, padding: 8 }} />
                  <button className="btn" onClick={enrollName}>{t("add")}</button>
                </div>
              </div>

              <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{t("roster")} ({booked.length}/{sel.capacity})</div>
              {booked.length === 0 && <p className="muted" style={{ marginTop: 4 }}>{t("empty_roster")}</p>}
              {booked.map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f2f4f6" }}>
                  <span style={{ flex: 1 }}>{e.name || "—"} {e.status === "attended" && <span style={{ color: "#2b8a3e", fontSize: 12 }}>✓ {t("attended")}</span>}</span>
                  {e.status !== "attended" && <button className="btn" onClick={() => attendE(e.id)} style={{ padding: "3px 8px" }}>{t("mark")}</button>}
                  <button className="btn" onClick={() => cancelE(e.id)} style={{ padding: "3px 8px" }}>{t("remove")}</button>
                </div>
              ))}

              {waiting.length > 0 && (
                <>
                  <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginTop: 12, color: "#b8860b" }}>{t("waitlist")} ({waiting.length})</div>
                  {waiting.map((e) => (
                    <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f2f4f6" }}>
                      <span style={{ minWidth: 22, fontWeight: 700, color: "#b8860b" }}>{e.position}</span>
                      <span style={{ flex: 1 }}>{e.name || "—"}</span>
                      <button className="btn" onClick={() => cancelE(e.id)} style={{ padding: "3px 8px" }}>{t("remove")}</button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
